import React from 'react';
import { Mail, Phone, MapPin, Globe, Clock, MessageSquare } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import Layout from '@/components/Layout';

const ContactUsPage = () => {
  return (
    <Layout>
      <div className="space-y-6 max-w-4xl">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" data-testid="contact-title">Contact Us</h1>
          <p className="text-slate-600 mt-1">Channel Partner Support</p>
        </div>

        <Card className="border-2 border-slate-200">
          <CardHeader>
            <CardTitle className="text-xl">We're Here to Help</CardTitle>
            <CardDescription>
              Get assistance with onboarding, referrals, commissions, and any questions related to the EliteRentals Channel Partner Program.
            </CardDescription>
          </CardHeader>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Email Support */}
          <Card data-testid="email-card">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Mail className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Email Support</CardTitle>
                  <CardDescription>We respond within 24 hours</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <a 
                href="mailto:contact@eliterentals.in" 
                className="text-blue-600 hover:underline font-medium"
              >
                contact@eliterentals.in
              </a>
            </CardContent>
          </Card>

          {/* Phone Support */}
          <Card data-testid="phone-card">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Phone className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Phone / WhatsApp</CardTitle>
                  <CardDescription>Available Mon-Sat</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              <a 
                href="tel:+919024849400" 
                className="text-green-600 hover:underline font-medium text-lg block"
              >
                +91-9024849400
              </a>
              <div className="flex items-start gap-2 text-sm text-slate-600">
                <Clock className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium">Support Hours:</p>
                  <p>Monday to Saturday</p>
                  <p>10:00 AM – 6:00 PM (IST)</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Office Address */}
        <Card data-testid="address-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <MapPin className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Office Address</CardTitle>
                <CardDescription>Visit us at our office</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <address className="not-italic text-slate-700 space-y-1">
              <p className="font-semibold text-slate-900">EliteRentals</p>
              <p>Tulip Lemon, Sector 69,</p>
              <p>Gurgaon, Haryana, India</p>
            </address>
          </CardContent>
        </Card>

        {/* Websites */}
        <Card data-testid="websites-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <Globe className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Our Websites</CardTitle>
                <CardDescription>Visit our online platforms</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-sm text-slate-600 mb-1">Main Website:</p>
              <a 
                href="https://eliterentals.in" 
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline font-medium"
              >
                https://eliterentals.in
              </a>
            </div>
            <div>
              <p className="text-sm text-slate-600 mb-1">Channel Partner Portal:</p>
              <a 
                href="https://partner.eliterentals.in" 
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline font-medium"
              >
                https://partner.eliterentals.in
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Support Information */}
        <Card className="bg-blue-50 border-blue-200">
          <CardHeader>
            <div className="flex items-center gap-3">
              <MessageSquare className="w-6 h-6 text-blue-600" />
              <CardTitle className="text-lg">Partner Support</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="font-medium text-slate-900 mb-2">We can help you with:</p>
              <ul className="list-disc list-inside space-y-1 text-slate-700">
                <li>Portal access or login issues</li>
                <li>Referral tracking</li>
                <li>Commission or payout queries</li>
                <li>Account approval or updates</li>
              </ul>
            </div>
            
            <div className="bg-white rounded-lg p-4 border border-blue-200">
              <p className="font-medium text-slate-900 mb-2">📝 Quick Note for Faster Resolution:</p>
              <p className="text-sm text-slate-700 mb-2">Please mention:</p>
              <ul className="list-disc list-inside space-y-1 text-sm text-slate-700">
                <li>Your registered mobile number</li>
                <li>Order ID (if applicable)</li>
              </ul>
              <p className="text-xs text-slate-600 mt-3 italic">
                * Commission-related queries are processed only for confirmed orders after the cooling period.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Footer Message */}
        <Card className="bg-gradient-to-r from-slate-900 to-slate-800 text-white">
          <CardContent className="pt-6 text-center">
            <h3 className="text-xl font-semibold mb-2">🙌 We Value Our Partners</h3>
            <p className="text-slate-200">
              At EliteRentals, we believe in long-term partnerships built on transparency and trust.
              Our team is always happy to assist you.
            </p>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default ContactUsPage;
